import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Globe, ListFilter, X } from 'lucide-react';
import CompactSingleSelectApiDropdown from './CompactSingleSelectApiDropdown';
import baseapi from '../services/baseapi';

type LocationItem = { id?: string | number | null;[key: string]: any } | null;

export interface LocationIds {
    regionId: string | number | null;
    stateId: string | number | null;
    districtId: string | number | null;
    townId: string | number | null;
    siteId?: string | number | null;
}

const DEFAULT_REGION = {
    id: 2,
    name: 'South India',
    code: 'SI',
};

const DEFAULT_STATE = {
    id: 1,
    region_id: 2,
    name: 'Andhra Pradesh',
    code: 'AP',
};

interface LocationSelectorSectionProps {
    ids: LocationIds;
    onIdsChange: (value: LocationIds) => void;
    className?: string;
    compact?: boolean;
    dropdownClassName?: string;
    showSites?: boolean;
}

const LocationSelectorSection: React.FC<LocationSelectorSectionProps> = ({
    ids,
    onIdsChange,
    className = '',
    showSites = false,
}) => {
    const [showFilters, setShowFilters] = useState(false);

    const [draftIds, setDraftIds] = useState<LocationIds>({
        regionId: ids.regionId ?? DEFAULT_REGION.id,
        stateId: ids.stateId ?? DEFAULT_STATE.id,
        districtId: ids.districtId ?? null,
        townId: ids.townId ?? null,
        siteId: ids.siteId ?? null,
    });

    const [selected, setSelected] = useState<{
        region: LocationItem;
        state: LocationItem;
        district: LocationItem;
        town: LocationItem;
        site: LocationItem;
    }>({
        region: DEFAULT_REGION,
        state: DEFAULT_STATE,
        district: null,
        town: null,
        site: null,
    });

    const appliedFiltersCount = useMemo(() => {
        let count = 0;
        if (ids.districtId) count++;
        if (ids.townId) count++;
        if (showSites && ids.siteId) count++;
        return count;
    }, [ids.districtId, ids.townId, ids.siteId, showSites]);

    useEffect(() => {
        setDraftIds({
            regionId: ids.regionId ?? DEFAULT_REGION.id,
            stateId: ids.stateId ?? DEFAULT_STATE.id,
            districtId: ids.districtId ?? null,
            townId: ids.townId ?? null,
            siteId: ids.siteId ?? null,
        });
    }, [ids]);

    // useEffect(() => {
    //     if (ids.districtId || ids.townId || (showSites && ids.siteId)) {
    //         setShowFilters(true);
    //     }
    // }, [ids.districtId, ids.townId, ids.siteId, showSites]);

    useEffect(() => {
        if (!showSites) {
            setSelected((prev) => ({ ...prev, site: null }));
            setDraftIds((prev) => ({ ...prev, siteId: null }));
        }
    }, [showSites]);

    useEffect(() => {
        if (draftIds.districtId) {
            if (!selected.district || selected.district.id !== draftIds.districtId) {
                const fetchDistrict = async () => {
                    try {
                        const query = JSON.stringify({ id: draftIds.districtId });
                        const res = await baseapi.get('/districts', { params: { query } });
                        if (res.data?.status && res.data?.data?.length > 0) {
                            setSelected((prev) => ({ ...prev, district: res.data.data[0] }));
                        }
                    } catch (e) {
                        console.error('Failed to fetch initial district details', e);
                    }
                };
                fetchDistrict();
            }
        } else {
            setSelected((prev) => ({ ...prev, district: null }));
        }
    }, [draftIds.districtId, selected.district]);

    useEffect(() => {
        if (draftIds.townId) {
            if (!selected.town || selected.town.id !== draftIds.townId) {
                const fetchTown = async () => {
                    try {
                        const query = JSON.stringify({ id: draftIds.townId });
                        const res = await baseapi.get('/towns', { params: { query } });
                        if (res.data?.status && res.data?.data?.length > 0) {
                            setSelected((prev) => ({ ...prev, town: res.data.data[0] }));
                        }
                    } catch (e) {
                        console.error('Failed to fetch initial town details', e);
                    }
                };
                fetchTown();
            }
        } else {
            setSelected((prev) => ({ ...prev, town: null }));
        }
    }, [draftIds.townId, selected.town]);

    useEffect(() => {
        if (!showSites) return;

        if (draftIds.siteId) {
            if (!selected.site || selected.site.id !== draftIds.siteId) {
                const fetchSite = async () => {
                    try {
                        const query = JSON.stringify({ id: draftIds.siteId });
                        const res = await baseapi.get('/clients', { params: { query } });
                        if (res.data?.status && res.data?.data?.length > 0) {
                            setSelected((prev) => ({ ...prev, site: res.data.data[0] }));
                        }
                    } catch (e) {
                        console.error('Failed to fetch initial site details', e);
                    }
                };
                fetchSite();
            }
        } else {
            setSelected((prev) => ({ ...prev, site: null }));
        }
    }, [draftIds.siteId, selected.site, showSites]);

    const updateSelection = (field: 'region' | 'state' | 'district' | 'town' | 'site', item: LocationItem) => {
        if (field === 'region') {
            setSelected({
                region: item,
                state: null,
                district: null,
                town: null,
                site: null,
            });
            setDraftIds({
                regionId: item?.id ?? null,
                stateId: null,
                districtId: null,
                townId: null,
                siteId: showSites ? null : draftIds.siteId,
            });
            return;
        }

        if (field === 'state') {
            setSelected((prev) => ({
                ...prev,
                state: item,
                district: null,
                town: null,
                site: null,
            }));
            setDraftIds((prev) => ({
                ...prev,
                stateId: item?.id ?? null,
                districtId: null,
                townId: null,
                siteId: showSites ? null : prev.siteId,
            }));
            return;
        }

        if (field === 'district') {
            setSelected((prev) => ({
                ...prev,
                district: item,
                town: null,
                site: null,
            }));
            setDraftIds((prev) => ({
                ...prev,
                districtId: item?.id ?? null,
                townId: null,
                siteId: showSites ? null : prev.siteId,
            }));
            return;
        }

        if (field === 'town') {
            setSelected((prev) => ({
                ...prev,
                town: item,
                site: null,
            }));
            setDraftIds((prev) => ({
                ...prev,
                townId: item?.id ?? null,
                siteId: showSites ? null : prev.siteId,
            }));
            return;
        }

        if (field === 'site') {
            setSelected((prev) => ({ ...prev, site: item }));
            setDraftIds((prev) => ({ ...prev, siteId: item?.id ?? null }));
            return;
        }
    };

    const handleApply = () => {
        onIdsChange(draftIds);
        setShowFilters(false);
    };

    const handleClear = () => {
        const cleared: LocationIds = {
            regionId: DEFAULT_REGION.id,
            stateId: DEFAULT_STATE.id,
            districtId: null,
            townId: null,
            siteId: null,
        };

        setDraftIds(cleared);
        setSelected({
            region: DEFAULT_REGION,
            state: DEFAULT_STATE,
            district: null,
            town: null,
            site: null,
        });
    };

    const draftAppliedCount = useMemo(() => {
        let count = 0;
        if (draftIds.districtId) count++;
        if (draftIds.townId) count++;
        if (showSites && draftIds.siteId) count++;
        return count;
    }, [draftIds.districtId, draftIds.townId, draftIds.siteId, showSites]);

    return (
        <div className={`${className}`}>
            <button
                type="button"
                onClick={() => setShowFilters(true)}
                className="relative flex items-center gap-2 px-2.5 py-1 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-[#422568] hover:border-[#422568] transition-colors"
            >
                <ListFilter className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">Filters</span>

                {appliedFiltersCount > 0 && (
                    <span className="ml-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-[#422568] px-1.5 text-[10px] font-bold text-white">
                        {appliedFiltersCount}
                    </span>
                )}
            </button>

            {createPortal(
                <div
                    className={`fixed inset-0 z-[9999] transition-all duration-300 ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'
                        }`}
                >
                    <div
                        className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${showFilters ? 'opacity-100' : 'opacity-0'
                            }`}
                        onClick={() => setShowFilters(false)}
                    />

                    <div
                        className={`absolute top-0 right-0 h-full w-80 bg-white shadow-2xl border-l border-slate-200 transition-transform duration-300 ease-in-out ${showFilters ? 'translate-x-0' : 'translate-x-full'
                            }`}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-2">
                                <ListFilter className="w-4 h-4 text-[#422568]" />
                                <h3 className="text-sm font-bold text-slate-800">Location Filters</h3>
                                {draftAppliedCount > 0 && (
                                    <span className="inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-[#422568] px-1.5 text-[10px] font-bold text-white">
                                        {draftAppliedCount}
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowFilters(false)}
                                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="p-4 space-y-4 overflow-y-auto h-[calc(100%-125px)]">
                            {/* <div>
                                <CompactSingleSelectApiDropdown
                                    label="Region"
                                    icon={Globe}
                                    apiEndpoint="/regions"
                                    value={selected.region}
                                    onChange={(val) => updateSelection('region', val)}
                                    placeholder="Select Region"
                                    searchInputPlaceholder="Search region..."
                                    displayProperty="name"
                                    searchFields={['name']}
                                    disabled={true}
                                    labelTextSize="text-[8px]"
                                />
                            </div>

                            <div>
                                <CompactSingleSelectApiDropdown
                                    label="State"
                                    icon={Globe}
                                    apiEndpoint="/states"
                                    disabled={true}
                                    value={selected.state}
                                    onChange={(val) => updateSelection('state', val)}
                                    placeholder={draftIds.regionId ? 'Select State' : 'Select region first'}
                                    searchInputPlaceholder="Search state..."
                                    displayProperty="name"
                                    searchFields={['name']}
                                    additionalQueries={{ region_id: draftIds.regionId }}
                                    labelTextSize="text-[8px]"
                                />
                            </div> */}

                            <div>
                                <CompactSingleSelectApiDropdown
                                    label="District"
                                    icon={Globe}
                                    apiEndpoint="/districts"
                                    disabled={!draftIds.stateId}
                                    value={selected.district}
                                    onChange={(val) => updateSelection('district', val)}
                                    placeholder={draftIds.stateId ? 'Select District' : 'Select state first'}
                                    searchInputPlaceholder="Search district..."
                                    displayProperty="name"
                                    searchFields={['name']}
                                    additionalQueries={{ state_id: draftIds.stateId }}
                                    labelTextSize="text-[8px]"
                                />
                            </div>

                            <div>
                                <CompactSingleSelectApiDropdown
                                    label="Town"
                                    icon={Globe}
                                    apiEndpoint="/towns"
                                    disabled={!draftIds.districtId}
                                    value={selected.town}
                                    onChange={(val) => updateSelection('town', val)}
                                    placeholder={draftIds.districtId ? 'Select Town' : 'Select district first'}
                                    searchInputPlaceholder="Search town..."
                                    displayProperty="name"
                                    searchFields={['name']}
                                    additionalQueries={{ district_id: draftIds.districtId }}
                                    labelTextSize="text-[8px]"
                                />
                            </div>

                            {showSites && (
                                <div>
                                    <CompactSingleSelectApiDropdown
                                        label="Site"
                                        icon={Globe}
                                        apiEndpoint="/clients"
                                        disabled={!draftIds.townId}
                                        value={selected.site}
                                        onChange={(val) => updateSelection('site', val)}
                                        placeholder={draftIds.townId ? 'Select Site' : 'Select town first'}
                                        searchInputPlaceholder="Search site..."
                                        displayProperty="name"
                                        searchFields={['name']}
                                        additionalQueries={{ town_id: draftIds.townId }}
                                        labelTextSize="text-[8px]"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="h-[64px] border-t border-slate-200 bg-white px-4 flex items-center justify-between gap-3">
                            <button
                                type="button"
                                onClick={handleClear}
                                className="px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                Clear
                            </button>

                            <button
                                type="button"
                                onClick={handleApply}
                                className="px-4 py-2 text-xs font-bold text-white bg-[#422568] rounded-lg hover:bg-[#422568] transition-colors"
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default LocationSelectorSection;